import { BlueprintProvider, Container, ContainerProps } from '@hover/blueprint';
import { space } from '@hover/blueprint/foundation';
import React, { FC } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Helmet from 'react-helmet';
import { Footer, Navbar } from '..';
import { ILinksGroup } from '../../../interfaces/ILinksGroup';
import { ILink } from '../../../interfaces/ILink';
import { INavbarItem } from '../../../interfaces/INavbarItems';
import HoverCookieConsent from './HoverCookieConsent';
import {
  HOVER_LINKEDIN_LING,
  HOVER_FACEBOOK_LINK,
  HOVER_TWITTER_LINK,
} from '../../../constants';
import SEO, { MetadataItem } from '../../../../components/Seo';

export const HEADER_HEIGHT = space[1000];

const navbarLinksDesktop: INavbarItem[] = [
  {
    label: 'Professionals',
    children: [
      {
        label: 'INDUSTRIES',
        children: [
          {
            label: 'Exterior contractors',
            to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors`,
          },
          {
            label: 'Insurance',
            to: `${process.env.GATSBY_V2_PREFIX}/insurance`,
          },
        ],
      },
      {
        label: 'INFORMATION',
        children: [
          {
            label: 'Pricing',
            to: `${process.env.GATSBY_V2_PREFIX}/pricing`,
          },
          {
            label: 'Tips and tricks',
            to: `${process.env.GATSBY_V2_PREFIX}/blog?from=tips`,
          },
        ],
      },
    ],
  },
  {
    label: 'Homeowners',
    children: [
      {
        label: 'I WAS INVITED TO USE HOVER',
        children: [
          {
            label: 'By my contractor',
            to: `${process.env.GATSBY_V2_PREFIX}/invited-by-contractor`,
          },
          {
            label: 'By my insurance company',
            to: `${process.env.GATSBY_V2_PREFIX}/invited-by-insurance`,
          },
        ],
      },
      {
        label: 'I WANT TO KNOW HOW IT WORKS',
        children: [
          {
            label: 'How it works',
            to: `${process.env.GATSBY_V2_PREFIX}/how-it-works`,
          },
        ],
      },
    ],
  },
  {
    label: 'About',
    children: [
      {
        label: 'About us',
        to: `${process.env.GATSBY_V2_PREFIX}/about`,
      },
      {
        label: 'Press',
        to: `${process.env.GATSBY_V2_PREFIX}/press`,
      },
      {
        label: 'Blog',
        to: `${process.env.GATSBY_V2_PREFIX}/blog`,
      },
    ],
  },
  { label: 'Careers', to: `${process.env.GATSBY_V2_PREFIX}/careers` },
];

const navbarLinksMobile: INavbarItem[] = [
  {
    label: 'Log in',
    to: `${process.env.GATSBY_V2_PREFIX}/login`,
  },
  {
    label: 'Professionals',
    children: [
      {
        label: 'INDUSTRIES',
        children: [
          {
            label: 'Exterior contractors',
            to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors`,
          },
          {
            label: 'Insurance',
            to: `${process.env.GATSBY_V2_PREFIX}/insurance`,
          },
        ],
      },
      {
        label: 'INFORMATION',
        children: [
          {
            label: 'Pricing',
            to: `${process.env.GATSBY_V2_PREFIX}/pricing`,
          },
          {
            label: 'Tips and tricks',
            to: `${process.env.GATSBY_V2_PREFIX}/blog?from=tips`,
          },
        ],
      },
    ],
  },
  {
    label: 'Homeowners',
    children: [
      {
        label: 'I WAS INVITED TO USE HOVER',
        children: [
          {
            label: 'By my insurance company',
            to: `${process.env.GATSBY_V2_PREFIX}/invited-by-insurance`,
          },
          {
            label: 'By my contractor',
            to: `${process.env.GATSBY_V2_PREFIX}/invited-by-contractor`,
          },
        ],
      },
      {
        label: 'I WANT TO KNOW HOW IT WORKS',
        children: [
          {
            label: 'How it works',
            to: `${process.env.GATSBY_V2_PREFIX}/how-it-works`,
          },
        ],
      },
    ],
  },
  {
    label: 'About',
    children: [
      {
        label: 'About us',
        to: `${process.env.GATSBY_V2_PREFIX}/about`,
      },
      {
        label: 'Press',
        to: `${process.env.GATSBY_V2_PREFIX}/press`,
      },
      {
        label: 'Blog',
        to: `${process.env.GATSBY_V2_PREFIX}/blog`,
      },
    ],
  },
  {
    label: 'Careers',
    to: `${process.env.GATSBY_V2_PREFIX}/careers`,
  },
];

const footerLinksDesktop: ILinksGroup = [
  {
    label: 'For Professionals',
    links: [
      {
        label: 'Exterior Contractors',
        to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors`,
      },
      { label: 'Insurance', to: `${process.env.GATSBY_V2_PREFIX}/insurance` },
      {
        label: 'Restoration',
        to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors-restoration`,
      },
      {
        label: 'Architects',
        to: `${process.env.GATSBY_V2_PREFIX}/measurement-app-architects`,
      },
      {
        label: 'Tax Assessors',
        to: `${process.env.GATSBY_V2_PREFIX}/tax-assessors`,
      },
      {
        label: 'Tips and tricks',
        to: `${process.env.GATSBY_V2_PREFIX}/blog?from=tips`,
      },
    ],
  },
  {
    label: 'Product',
    links: [
      { label: 'Pricing', to: `${process.env.GATSBY_V2_PREFIX}/pricing` },
      {
        label: 'HOVER Reports',
        to: `${process.env.GATSBY_V2_PREFIX}/measurements_overview`,
      },
      {
        label: 'Sales solutions',
        to: `${process.env.GATSBY_V2_PREFIX}/product`,
      },
      {
        label: 'Production tools',
        to: `${process.env.GATSBY_V2_PREFIX}/transform-plan`,
      },
      { label: 'Integrations', to: `${process.env.GATSBY_V2_PREFIX}/partners` },
    ],
  },
  {
    label: 'For Homeowners',
    links: [
      {
        label: 'How It Works',
        to: `${process.env.GATSBY_V2_PREFIX}/how-it-works`,
      },
      {
        label: 'Invited by a contractor',
        to: `${process.env.GATSBY_V2_PREFIX}/invited-by-contractor`,
      },
      {
        label: 'Invited by an insurer',
        to: `${process.env.GATSBY_V2_PREFIX}/invited-by-insurance`,
      },
    ],
  },
  {
    label: 'Resources',
    links: [
      { label: 'Blog', to: `${process.env.GATSBY_V2_PREFIX}/blog` },
      { label: 'Support', to: '' },
      { label: 'Developers', to: '' },
      { label: 'Trust Center', to: '' },
      { label: 'Marketing toolkits', to: '' },
    ],
  },
  {
    label: 'company',
    links: [
      { label: 'About', to: `${process.env.GATSBY_V2_PREFIX}/about` },
      { label: 'Media', to: `${process.env.GATSBY_V2_PREFIX}/press` },
      { label: 'Work at HOVER', to: `${process.env.GATSBY_V2_PREFIX}/careers` },
      { label: 'Contact us', to: `${process.env.GATSBY_V2_PREFIX}/contact` },
      { label: 'Marketing toolkits', to: '' },
    ],
  },
];

const footerLinksMobile: ILinksGroup = [
  {
    label: 'For Professionals',
    links: [
      {
        label: 'Exterior Contractors',
        to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors`,
      },
      { label: 'Insurance', to: `${process.env.GATSBY_V2_PREFIX}/insurance` },
      {
        label: 'Restoration',
        to: `${process.env.GATSBY_V2_PREFIX}/exterior-contractors-restoration`,
      },
      {
        label: 'Architects',
        to: `${process.env.GATSBY_V2_PREFIX}/measurement-app-architects`,
      },
      {
        label: 'Tax Assessors',
        to: `${process.env.GATSBY_V2_PREFIX}/tax-assessors`,
      },
      {
        label: 'Tips and tricks',
        to: `${process.env.GATSBY_V2_PREFIX}/blog?from=tips`,
      },
    ],
  },
  {
    label: 'For Homeowners',
    links: [
      {
        label: 'How It Works',
        to: `${process.env.GATSBY_V2_PREFIX}/how-it-works`,
      },
      {
        label: 'Invited by a contractor',
        to: `${process.env.GATSBY_V2_PREFIX}/invited-by-contractor`,
      },
      {
        label: 'Invited by an insurer',
        to: `${process.env.GATSBY_V2_PREFIX}/invited-by-insurance`,
      },
    ],
  },
  {
    label: 'Product',
    links: [
      { label: 'Pricing', to: `${process.env.GATSBY_V2_PREFIX}/pricing` },
      {
        label: 'HOVER Reports',
        to: `${process.env.GATSBY_V2_PREFIX}/measurements_overview`,
      },
      {
        label: 'Sales solutions',
        to: `${process.env.GATSBY_V2_PREFIX}/product`,
      },
      {
        label: 'Production tools',
        to: `${process.env.GATSBY_V2_PREFIX}/transform-plan`,
      },
      { label: 'Integrations', to: `${process.env.GATSBY_V2_PREFIX}/partners` },
    ],
  },
  {
    label: 'Resources',
    links: [
      { label: 'Blog', to: `${process.env.GATSBY_V2_PREFIX}/blog` },
      { label: 'Support', to: '' },
      { label: 'Developers', to: '' },
      { label: 'Trust Center', to: '' },
      { label: 'Marketing toolkits', to: '' },
    ],
  },
  {
    label: 'company',
    links: [
      { label: 'About', to: `${process.env.GATSBY_V2_PREFIX}/about` },
      { label: 'Media', to: `${process.env.GATSBY_V2_PREFIX}/press` },
      { label: 'Work at HOVER', to: `${process.env.GATSBY_V2_PREFIX}/careers` },
      { label: 'Contact us', to: `${process.env.GATSBY_V2_PREFIX}/contact` },
      { label: 'Marketing toolkits', to: '' },
    ],
  },
];

const footerBottomItems: Array<ILink> = [
  {
    to: `${process.env.GATSBY_V2_PREFIX}/privacy`,
    label: 'Privacy',
  },
  {
    to: `${process.env.GATSBY_V2_PREFIX}/terms_of_use`,
    label: 'Terms of use',
  },
  {
    to: `${process.env.GATSBY_V2_PREFIX}/`,
    label: 'Newsletter',
  },
  {
    to: HOVER_TWITTER_LINK,
    label: 'Twitter',
  },
  {
    to: HOVER_FACEBOOK_LINK,
    label: 'Facebook',
  },
  {
    to: HOVER_LINKEDIN_LING,
    label: 'Linkedin',
  },
];

type ButtonTypes = 'DOWNLOAD_APP' | 'CHAT_WITH_A_REP' | 'SCHEDULE_A_DEMO';

interface Props extends ContainerProps {
  isNavbarWhite?: boolean;
  navbarBackgroundColor?: string;
  showDivider?: boolean;
  dividerColor?: string;
  buttonType?: ButtonTypes;
  title?: string;
  description?: string;
  additionalMetadata?: MetadataItem[];
  noIndex?: boolean;
}

const latestArticles = graphql`
  query getLatestBlogArticles {
    allContentfulBlogPost(
      sort: { fields: publishDate, order: DESC }
      limit: 2
    ) {
      nodes {
        title
        slug
      }
    }
  }
`;

interface ILatestBlogArticles {
  allContentfulBlogPost: {
    nodes: { title: string; slug: string }[];
  };
}

const Layout: FC<Props> = ({
  children,
  isNavbarWhite,
  navbarBackgroundColor,
  showDivider,
  dividerColor,
  buttonType,
  title = '',
  description = '',
  noIndex = false,
  additionalMetadata = [],
  ...restProps
}) => {
  const {
    allContentfulBlogPost: { nodes },
  }: ILatestBlogArticles = useStaticQuery(latestArticles);

  const articleLinks = {
    label: 'ARTICLES',
    links: [
      {
        label: nodes[0].title,
        to: `${process.env.GATSBY_V2_PREFIX}/blog/${nodes[0].slug}`,
      },
      {
        label: nodes[1].title,
        to: `${process.env.GATSBY_V2_PREFIX}/blog/${nodes[1].slug}`,
      },
    ],
  };

  return (
    <BlueprintProvider resetCSS includeFonts newTypography>
      <Helmet>
        {process.env.ENVIRONMENT === 'production' && (
          <script
            async
            src="https://awwtag.emaww.com/js?token=AWW-382978285-1"
          />
        )}
      </Helmet>
      <SEO
        title={title}
        description={description}
        additionalMetadata={additionalMetadata}
        noIndex={noIndex}
      />
      <Navbar
        showDivider={!!showDivider}
        linksDesktop={navbarLinksDesktop}
        linksMobile={navbarLinksMobile}
        isNavbarWhite={!!isNavbarWhite}
        navbarBackgroundColor={navbarBackgroundColor}
        dividerColor={dividerColor}
        buttonType={buttonType ?? 'DOWNLOAD_APP'}
      />
      <Container
        centerContent
        maxW="100%"
        px={0}
        pt={{ base: 900, desktop: 1000 }}
        {...restProps}
      >
        {children}
      </Container>
      <Footer
        linksDesktop={[...footerLinksDesktop, articleLinks]}
        linksMobile={[...footerLinksMobile, articleLinks]}
        footerBottomItems={footerBottomItems}
      />
      <HoverCookieConsent />
    </BlueprintProvider>
  );
};

Layout.defaultProps = {
  navbarBackgroundColor: 'neutral.0',
  isNavbarWhite: true,
  showDivider: true,
  dividerColor: 'primary.100',
  buttonType: 'DOWNLOAD_APP',
};

export default Layout;
